<template>
  <div :class="{ 'disabled-area': !$can('driver_edit') }">
    <v-form ref="driverForm" lazy-validation>
      <!-- DRIVER INFORMATION -->
      <v-row no-gutters class="my-5">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="credentials.name"
            :label="$t('common.fields.fullName') + ' *'"
            :placeholder="$t('common.fields.fullNamePlaceholder')"
            :rules="rules.name"
            :disabled="isNameDisabled"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.phone"
            :label="$t('common.fields.phone') + ' *'"
            :placeholder="$t('common.fields.phonePlaceholder')"
            :rules="rules.phone"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.inn"
            :label="$t('common.fields.idNumber') + ' *'"
            :placeholder="$t('common.fields.idNumberPlaceholder')"
            :rules="rules.inn"
            v-mask="'############'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.birthday"
            :label="$t('common.fields.birthday') + ' *'"
            :rules="rules.birthday"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-select
            v-model="credentials.insurance_class_rule_id"
            class="ma-2"
            item-text="name"
            item-value="id"
            :items="insuranceClassList"
            :rules="rules.insuranceClass"
            :label="$t('driverRating.configurtor.insurance') + ' *'"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            :items="regions"
            v-model="credentials.locationId"
            :label="$t('common.fields.region') + ' *'"
            item-text="title"
            item-value="id"
            :hide-details="false"
            class="ma-2"
          ></v-select>
          <v-text-field
            v-model="credentials.address"
            :label="$t('common.fields.address') + ' *'"
            :rules="rules.address"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.kaspi_number"
            :label="$t('driverAdd.kaspi')"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.secondPhone"
            :label="$t('common.fields.extraPhone')"
            :placeholder="$t('common.fields.phonePlaceholder')"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-textarea
            v-model="credentials.statusComment"
            class="ma-2 status-comment"
            no-resize
            :label="$t('driversList.tableHeaders.statusComment')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div v-if="isEdit" class="ma-2">
            <h3 class="mb-2">{{ $t('common.blocks.password') + ' *' }}</h3>
            <v-label>**********</v-label>
            <div class="mt-3">
              <v-btn
                type="button"
                class="warning mr-5"
                depressed
                @click="changePasswordDialog = true"
                >{{ $t('setPassword.button') }}</v-btn
              >
            </div>
          </div>
          <div v-else class="ma-2">
            <h3 class="weight">{{ $t('common.blocks.password') + ' *' }}</h3>
            <v-text-field
              name="password"
              :placeholder="$t('common.fields.passwordPlaceholder') + '*'"
              v-model="credentials.password"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              :rules="rules.password"
              @click:append="showPassword = !showPassword"
              required
            />
            <v-text-field
              name="repeatPassword"
              :placeholder="$t('common.fields.repeatPlaceholder') + '*'"
              v-model="credentials.passwordConfirmation"
              :append-icon="
                showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'
              "
              :type="showPasswordConfirmation ? 'text' : 'password'"
              :rules="rules.repeatPassword"
              @click:append="
                showPasswordConfirmation = !showPasswordConfirmation
              "
              required
            />
          </div>

          <v-select
            :items="depositList"
            v-model="credentials.deposit_type"
            :label="$t('common.fields.depositType') + ' *'"
            item-text="title"
            item-value="id"
            :hide-details="false"
            class="ma-2"
          ></v-select>

          <v-text-field v-if="credentials.deposit_type"
            v-model="credentials.deposit_str"
            :label="$t('common.fields.depositStr.'+credentials.deposit_type) + ' *'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <!-- <v-checkbox
            color="primary"
            v-model="credentials.isActive"
            :label="$t('common.fields.isDriverTrusted')"
            class="ma-2"
            :input-value="credentials.isActive"
          ></v-checkbox> -->
          <!-- <v-checkbox
            color="primary"
            v-model="credentials.allowNegativeBalance"
            :label="$t('common.fields.allowNegativeBalance')"
            class="ma-2"
            :input-value="credentials.allowNegativeBalance"
          ></v-checkbox> -->
        </v-col>
      </v-row>
      <hr />
      <!-- LICENSE BLOCK -->
      <v-row no-gutters class="my-5">
        <v-col cols="12" md="4">
          <h3 class="weight ma-2">
            {{ $t('common.blocks.driverLicense') + ' *' }}
          </h3>
          <v-text-field
            v-model="credentials.drivingLicense"
            :label="$t('common.fields.drivingLicense') + ' *'"
            :rules="rules.drivingLicense"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.obtainingLicense"
            :label="$t('common.fields.obtainingLicense') + ' *'"
            :rules="rules.obtainingLicense"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.expiryLicense"
            :label="$t('common.fields.expiryLicense') + ' *'"
            :rules="rules.expiryLicense"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.driving_experience_date"
            :label="$t('driver.licenseDate') + ' *'"
            :rules="rules.licenseDate"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-row class="d-md-flex">
            <v-col cols="12" md="6" sm="12" class="img-uploader">
              <image-uploader-new
                ref="licensePhoto"
                :photo.sync="credentials.drivingLicensePhoto1"
                :rules="rules.drivingLicensePhoto1"
              />
            </v-col>
            <v-col cols="12" md="6" sm="12" class="img-uploader">
              <image-uploader-new
                :photo.sync="credentials.drivingLicensePhoto2"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="12" :class="{ 'error-hint': licensePhotoError }">
              <v-label>
                {{ $t('driver.license.photo.requiredHint') }}
              </v-label>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <hr />
      <!-- PASSPORT BLOCK -->
      <v-row no-gutters class="my-5">
        <v-col cols="12" md="4">
          <h3 class="weight ma-2">{{ $t('common.blocks.document') + ' *' }}</h3>
          <v-select
            :items="docTypes"
            v-model="credentials.personalityDocKey"
            :label="$t('common.fields.docType') + ' *'"
            item-text="title"
            item-value="id"
            :hide-details="false"
            class="ma-2"
          ></v-select>
          <v-text-field
            v-model="credentials.personalityDocNumber"
            :label="$t('common.fields.personalityDocNumber') + ' *'"
            :rules="rules.personalityDocNumber"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.personalityDocIssuedBy"
            :label="$t('common.fields.personalityDocIssuedBy') + ' *'"
            :rules="rules.personalityDocIssuedBy"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.obtainingPersonalityDoc"
            :label="$t('common.fields.obtainingLicense') + ' *'"
            :rules="rules.obtainingPersonalityDoc"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.expiryPersonalityDoc"
            :label="$t('common.fields.expiryLicense') + ' *'"
            :rules="rules.expiryPersonalityDoc"
            v-mask="'##.##.####'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-row class="d-md-flex">
            <v-col cols="12" md="6" sm="12" class="img-uploader">
              <image-uploader-new
                ref="personalityDocPhoto"
                :photo.sync="credentials.personalityDocPhoto1"
                :rules="rules.personalityDocPhoto"
              />
            </v-col>
            <v-col cols="12" md="6" sm="12" class="img-uploader">
              <image-uploader-new
                :photo.sync="credentials.personalityDocPhoto2"
                :rules="rules.personalityDocPhoto"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="12" :class="{ 'error-hint': personalityDocPhotoError }">
              <v-label>
                {{ $t('driver.document.photo.requiredHint') }}
              </v-label>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <hr />
      <!-- RELATION BLOCK -->
      <v-row no-gutters class="my-5">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="credentials.nameRelative"
            :label="$t('common.fields.nameRelative') + ' *'"
            :placeholder="$t('common.fields.fullNamePlaceholder')"
            :rules="rules.nameRelative"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.phoneRelative"
            :label="$t('common.fields.phoneRelative') + ' *'"
            :placeholder="$t('common.fields.phonePlaceholder')"
            :rules="rules.phone"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
          <v-text-field
            v-model="credentials.relationship"
            :label="$t('common.fields.relationship') + ' *'"
            :placeholder="$t('common.fields.relationshipPlaceholder')"
            :rules="rules.relationship"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-textarea
            v-model="credentials.comment"
            outlined
            :label="$t('common.fields.comment')"
            :rules="rules.comment"
            class="ma-2"
          ></v-textarea>
        </v-col>
      </v-row>
      <!-- SUBMIT  & BLOCK BUTTON -->
      <v-row class="d-flex align-end flex-column">
        <v-flex id="alerts" xs12>
          <!-- Error Alert -->
          <alert-error class="px-3" />
          <!-- Success Alert-->
          <v-layout
            v-if="successAlert.state"
            row
            class="alert-wrapper mt-2 mb-2 px-3"
          >
            <v-flex sm12>
              <v-alert :value="successAlert.state" type="success">
                {{ successAlert.message }}
              </v-alert>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12>
          <v-btn
            v-if="isEdit && $can('driver_edit')"
            type="button"
            :disabled="areBtnsDisabled"
            class="error mr-5 mb-4"
            depressed
            large
            @click="changeBlockStatus"
          >
            {{
              credentials.isBlocked
                ? $t('common.btns.unblock')
                : $t('common.btns.block')
            }}
          </v-btn>
          <v-btn
            v-if="$can('driver_edit')"
            type="button"
            :disabled="areBtnsDisabled"
            class="warning mr-5 mb-4"
            depressed
            large
            @click="saveDriver"
            >{{ $t('common.btns.save') }}</v-btn
          >
        </v-flex>
      </v-row>
      <v-overlay :value="isLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-form>
    <change-password-dialog
      v-if="isEdit"
      :dialog.sync="changePasswordDialog"
      :driverId="driverId"
    />
    <block-driver-dialog @changeBlockState="changeBlockState" />
  </div>
</template>

<script>
/* eslint-disable */

import { mask } from 'vue-the-mask';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ChangePasswordDialog from '@/components/user/ChangePasswordDialog.vue';
import AlertError from '@/components/shared/AlertError.vue';
import ImageUploaderNew from '@/components/shared/ImageUploader.vue';
import BlockDriverDialog from '@/components/driver/BlockDriverDialog.vue';
import MenuDatepicker from '@/components/shared/MenuDatepicker.vue';

export default {
  name: 'AddDriverForm',
  directives: {
    mask,
  },
  components: {
    ImageUploaderNew,
    AlertError,
    ChangePasswordDialog,
    BlockDriverDialog,
    MenuDatepicker,
  },
  data() {
    return {
      on: false,
      isEdit: false,
      credentials: {
        name: null,
        phone: null,
        inn: '',
        birthday: null,
        locationId: 12,
        address: '',
        secondPhone: '',
        statusComment: '',
        insurance_class_rule_id: '',
        password: null,
        passwordConfirmation: null,
        isActive: false,
        allowNegativeBalance: false,
        drivingLicense: '',
        obtainingLicense: null,
        expiryLicense: null,
        driving_experience_date: '',
        drivingLicensePhoto1: null,
        drivingLicensePhoto2: null,
        personalityDocKey: 1,
        personalityDocNumber: '',
        personalityDocIssuedBy: '',
        obtainingPersonalityDoc: null,
        expiryPersonalityDoc: null,
        personalityDocPhoto1: null,
        personalityDocPhoto2: null,
        nameRelative: '',
        phoneRelative: '',
        relationship: '',
        comment: '',
        kaspi_number: '',
      },
      rules: {
        name: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,150}$/.test(
              v,
            ) || this.$t('common.fields.validation.fullName'),
        ],
        phone: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[+][0-9]{9,12}$/.test(v) ||
            this.$t('common.fields.validation.phone'),
        ],
        secondPhone: [
          (v) =>
            /^[+][0-9]{0,12}$/.test(v) ||
            this.$t('common.fields.validation.phone'),
        ],
        inn: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[0-9]{12,12}$/.test(v) ||
            this.$t('common.fields.validation.idNumber'),
        ],
        birthday: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() > this.getDateObj(v) ||
            this.$t('common.fields.validation.birthday'),
        ],
        licenseDate: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() > this.getDateObj(v) ||
            this.$t('common.fields.validation.licenseDate'),
        ],
        address: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*,)(+=._-]{1,150}$/.test(
              v,
            ) || this.$t('common.fields.validation.address'),
        ],
        password: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!@#$%^&*)(+=._-]{4,}$/.test(
              v,
            ) || this.$t('common.fields.validation.driverPassword'),
        ],
        repeatPassword: [
          (v) =>
            v === this.credentials.password ||
            this.$t('common.fields.validation.password.dontMatch'),
        ],
        drivingLicense: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,10}$/.test(
              v,
            ) || this.$t('common.fields.validation.drivingLicense'),
        ],
        drivingLicensePhoto1: [
          (v) =>
            !!v ||
            this.credentials.drivingLicensePhoto2 ||
            this.$t('common.fields.validation.photo.required'),
        ],
        personalityDocPhoto: [
          (v) => !!v || this.$t('common.fields.validation.photo.required'),
        ],
        obtainingLicense: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() > this.getDateObj(v) ||
            this.$t('common.fields.validation.obtainedDoc'),
        ],
        expiryLicense: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() < this.getDateObj(v) ||
            this.$t('common.fields.validation.expiredDoc'),
          (v) =>
            this.getDateObj(this.credentials.obtainingLicense) <
              this.getDateObj(v) ||
            this.$t('common.fields.validation.expiryDate'),
        ],
        personalityDocNumber: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,10}$/.test(
              v,
            ) || this.$t('common.fields.validation.personalityDocNumber'),
        ],
        personalityDocIssuedBy: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%,/-^&*)(+=._-]{1,150}$/.test(
              v,
            ) || this.$t('common.fields.validation.personalityDocIssuedBy'),
        ],
        obtainingPersonalityDoc: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() > this.getDateObj(v) ||
            this.$t('common.fields.validation.obtainedDoc'),
        ],
        expiryPersonalityDoc: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            (/^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
              v,
            ) &&
              this.isValidDate(this.getDateObj(v))) ||
            this.$t('common.fields.validation.dateFormat'),
          (v) =>
            new Date() < this.getDateObj(v) ||
            this.$t('common.fields.validation.expiredDoc'),
          (v) =>
            this.getDateObj(this.credentials.obtainingPersonalityDoc) <
              this.getDateObj(v) ||
            this.$t('common.fields.validation.expiryDate'),
        ],
        nameRelative: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,150}$/.test(
              v,
            ) || this.$t('common.fields.validation.fullName'),
        ],
        phoneRelative: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^[+][0-9]{9,12}$/.test(v) ||
            this.$t('common.fields.validation.phone'),
        ],
        relationship: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) =>
            /^.{1,150}$/.test(v) ||
            this.$t('common.fields.validation.relationship'),
        ],
        comment: [
          (v) =>
            /^.{0,300}$/.test(v) || this.$t('common.fields.validation.comment'),
        ],
        insuranceClass: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
        ],
      },
      showPassword: false,
      showPasswordConfirmation: false,
      isProcessForm: false,
      licensePhotoError: false,
      personalityDocPhotoError: false,
      changePasswordDialog: false,
      photoErrorText: null,
    };
  },
  props: {
    driverId: String,
  },
  computed: {
    ...mapGetters({
      depositList: 'driver/getDepositList',
      regions: 'driver/getDriversLocations',
      docTypes: 'driver/getDriversPersonalityDocs',
      successAlert: 'global/getAlertSuccess',
      isLoading: 'global/getLoader',
      imagesUploading: 'global/getImagesUploadingLength',
    }),
    areBtnsDisabled() {
      return this.isProcessForm || this.imagesUploading > 0;
    },
    isNameDisabled() {
      const isEdit = Boolean(this.driverId);
      return isEdit && !this.$can('driver_edit_name');
    },
    insuranceClassList() {
      const { configuratorList } = this.$store.state.driver;
      return configuratorList.filter(({ type }) => type === 1);
    },
  },
  watch: {
    // eslint-disable-next-line
    'credentials.drivingLicensePhoto1': function(newVal) {
      if (newVal !== null) {
        this.licensePhotoError = false;
      }
    },
    // eslint-disable-next-line
    'credentials.drivingLicensePhoto2': function(newVal) {
      if (newVal !== null) {
        this.licensePhotoError = false;
      }
    },
    // eslint-disable-next-line
    'credentials.personalityDocPhoto1': function(newVal) {
      if (this.credentials.personalityDocPhoto2 !== null && newVal !== null) {
        this.personalityDocPhotoError = false;
      }
    },
    // eslint-disable-next-line
    'credentials.personalityDocPhoto2': function(newVal) {
      if (this.credentials.personalityDocPhoto1 !== null && newVal !== null) {
        this.personalityDocPhotoError = false;
      }
    },
  },
  beforeCreate() {
    this.$store.commit('global/setLoader', true);
  },
  async created() {
    await this.fetchDriversProperties();
    await this.fetchRatingConfigurator();
    if (this.driverId) {
      // edit driver form
      try {
        // fetch driver data
        const driverResponse = await this.getDriverData(this.driverId);
        if (driverResponse.success && driverResponse.data) {
          this.isEdit = true;
          const driverCredentials = this.prepareInputData(driverResponse.data);
          this.credentials = driverCredentials;
          this.$refs.driverForm.resetValidation();
        }
      } catch (error) {
        if (error.status === 404) {
          this.$router.push({ name: 'not-found' });
        }
        this.setLoader(false);
      }
    }
    this.setLoader(false);
  },
  destroyed() {
    this.clearAlertError();
    this.clearAlertSuccess();
  },
  methods: {
    ...mapActions({
      fetchDriversProperties: 'driver/fetchDriversProperties',
      fetchRatingConfigurator: 'driver/fetchRatingConfigurator',
      createDriver: 'driver/createDriver',
      editDriver: 'driver/editDriver',
      getDriverData: 'driver/fetchDriverData',
    }),
    ...mapMutations({
      clearAlertError: 'global/clearAlertError',
      clearAlertSuccess: 'global/clearAlertSuccess',
      setAlertSuccess: 'global/setAlertSuccess',
      setLoader: 'global/setLoader',
      setBlockDriver: 'driver/setBlockDriver',
      setBlockDriverDialogState: 'driver/setBlockDriverDialogState',
    }),
    async saveDriver() {
      const isValidForm = this.$refs.driverForm.validate();
      const isValidPhotos = this.validatePhotos();
      if (isValidForm && isValidPhotos) {
        this.isProcessForm = true;
        const outData = this.prepareOutData();
        try {
          let response = null;
          if (this.isEdit && this.driverId) {
            delete outData.password;
            delete outData.passwordConfirmation;
            const driverData = {
              driverId: this.driverId,
              data: outData,
            };
            response = await this.editDriver(driverData);
          } else {
            response = await this.createDriver(outData);
          }
          if (response.success) {
            const successAlert = {
              state: true,
              message: this.$t('driver.saving.success'),
            };
            if (response.data && response.data.message) {
              successAlert.message = response.data.message;
            }
            this.clearAlertError();
            this.setAlertSuccess(successAlert);
            const that = this;
            setTimeout(() => {
              that.$router.push({ name: 'drivers-list' });
            }, 2500);
          }
        } catch (error) {
          this.isProcessForm = false;
        }
      }
    },
    // call validate method on image-uploader-new components
    validatePhotos() {
      let isValid = true;
      this.$children[0].$children.forEach((element) => {
        // eslint-disable-next-line
        if (
          element.$options['_componentTag'] &&
          element.$options['_componentTag'] === 'image-uploader-new'
        ) {
          if (!element.validate()) {
            isValid = false;
          }
        }
      });
      return isValid;
    },
    prepareOutData() {
      const data = { ...this.credentials };
      if (data.secondPhone === '+7') {
        data.secondPhone = null;
      }
      data.obtainingLicense = this.prepareDateToSend(data.obtainingLicense);
      data.expiryLicense = this.prepareDateToSend(data.expiryLicense);
      data.birthday = this.prepareDateToSend(data.birthday);
      data.driving_experience_date = this.prepareDateToSend(
        data.driving_experience_date,
      );
      data.obtainingPersonalityDoc = this.prepareDateToSend(
        data.obtainingPersonalityDoc,
      );
      data.expiryPersonalityDoc = this.prepareDateToSend(
        data.expiryPersonalityDoc,
      );
      // prepare photos
      const licensePhotos = [];
      if (data.drivingLicensePhoto1) {
        licensePhotos.push(data.drivingLicensePhoto1.name);
      }
      if (data.drivingLicensePhoto2) {
        licensePhotos.push(data.drivingLicensePhoto2.name);
      }
      delete data.drivingLicensePhoto1;
      delete data.drivingLicensePhoto2;
      data.drivingLicensePhoto = licensePhotos;

      const personalityDocPhotos = [];
      if (data.personalityDocPhoto1) {
        personalityDocPhotos.push(data.personalityDocPhoto1.name);
      }
      if (data.personalityDocPhoto2) {
        personalityDocPhotos.push(data.personalityDocPhoto2.name);
      }
      delete data.personalityDocPhoto1;
      delete data.personalityDocPhoto2;
      data.personalityDocPhoto = personalityDocPhotos;

      return data;
    },
    prepareInputData(data) {
      const outData = data;
      delete outData.id;
      delete outData.userId;
      delete outData.balance;
      delete outData.createdAt;
      delete outData.updatedAt;
      outData.expiryLicense = this.prepareDateFromAPI(outData.expiryLicense);
      outData.obtainingLicense = this.prepareDateFromAPI(
        outData.obtainingLicense,
      );
      outData.birthday = this.prepareDateFromAPI(outData.birthday);
      outData.driving_experience_date = this.prepareDateFromAPI(
        outData.driving_experience_date,
      );
      outData.expiryPersonalityDoc = this.prepareDateFromAPI(
        outData.expiryPersonalityDoc,
      );
      outData.obtainingPersonalityDoc = this.prepareDateFromAPI(
        outData.obtainingPersonalityDoc,
      );
      outData.driving_experience_date = data.driving_experience_date;
      outData.insurance_class_rule_id = data.insurance_class_rule_id;
      // eslint-disable-next-line
      outData.isActive = data.isActive ? true : false;
      // eslint-disable-next-line
      outData.allowNegativeBalance = data.allowNegativeBalance ? true : false;
      // working with photos
      if (outData.drivingLicensePhoto.length) {
        outData.drivingLicensePhoto.forEach((photo, index) => {
          const key = `drivingLicensePhoto${index + 1}`;
          outData[key] = photo;
        });
      }
      delete outData.drivingLicensePhoto;
      if (outData.personalityDocPhoto.length) {
        outData.personalityDocPhoto.forEach((photo, index) => {
          const key = `personalityDocPhoto${index + 1}`;
          outData[key] = photo;
        });
      }
      delete outData.personalityDocPhoto;
      return outData;
    },
    getDateObj(dateString) {
      if (dateString) {
        const [day, month, year] = dateString.split('.');
        const date = new Date(`${year}-${month}-${day}`);
        return date;
      }
      return dateString;
    },
    isValidDate(dateObject) {
      if (dateObject.getTime()) {
        return true;
      }
      return false;
    },
    prepareDateToSend(dateString) {
      if (dateString) {
        const [day, month, year] = dateString.split('.');
        const date = `${year}-${month}-${day}`;
        return date;
      }
      return dateString;
    },
    // convert date string from YYY-MM-DD format to DD.MM.YYYY
    prepareDateFromAPI(dateString) {
      if (dateString) {
        const [year, month, day] = dateString.split('-');
        const date = `${day}.${month}.${year}`;
        return date;
      }
      return dateString;
    },
    changeBlockStatus() {
      this.setBlockDriver({
        driverId: this.$route.params.driverId,
        isBlocked: this.credentials.isBlocked,
      });
      this.setBlockDriverDialogState(true);
    },
    changeBlockState() {
      this.credentials.isBlocked = +!this.credentials.isBlocked;
    },
  },
};
</script>

<style lang="scss" scoped>
h3 {
  margin-top: 15px;
  font-weight: 300;
}
.row {
  margin-left: 0px;
}
.img-uploader {
  height: 250px;
}
.error-hint label {
  color: red;
}
.status-comment {
  ::v-deep textarea {
    height: 100px;
  }
}
</style>
